// ============================================
// DOWNLOAD SERVICE - Handle downloads with proper auth and error handling
// ============================================

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// Storage key must match apiService.js
const TOKEN_STORAGE_KEY = 'unity-auth-token';

/**
 * Get JWT token from localStorage
 */
const getToken = () => {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY) || '';
  } catch {
    return '';
  }
};

/**
 * Generic download handler with error handling
 * @param {string} url - Full URL to download from
 * @param {string} filename - Suggested filename for the download
 * @param {boolean} requiresAuth - Whether the download requires authentication
 * @param {Function} onProgress - Optional callback for download progress (e.g., (loaded, total) => {})
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
const downloadFile = async (url, filename, requiresAuth = false, onProgress = null) => {
  try {
    console.log('[Download] Starting download:', { url, filename, requiresAuth });
    
    // Prepare headers
    const headers = {};
    if (requiresAuth) {
      const token = getToken();
      console.log('[Download] Token present:', !!token, 'Length:', token?.length);
      
      if (!token) {
        console.error('[Download] No token found for authenticated download');
        return {
          success: false,
          error: 'You need to be authenticated to download this file.'
        };
      }
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Fetch with proper headers
    console.log('[Download] Fetching with headers:', Object.keys(headers));
    const response = await fetch(url, {
      method: 'GET',
      headers,
      credentials: 'omit', // Don't send cookies
      mode: 'cors'
    });

    console.log('[Download] Response status:', response.status, response.statusText);

    // Check if response is OK
    if (!response.ok) {
      let errorMessage = 'Download failed.';
      
      // Try to parse error from API
      try {
        const errorData = await response.json();
        console.error('[Download] API error response:', errorData);
        if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (parseError) {
        // If can't parse JSON, use status text
        console.error('[Download] Failed to parse error response:', parseError);
        errorMessage = `Error ${response.status}: ${response.statusText}`;
      }

      // Specific error messages based on status
      if (response.status === 401) {
        errorMessage = 'Session expired. Please login again.';
      } else if (response.status === 403) {
        errorMessage = 'You do not have permission to access this file.';
      } else if (response.status === 404) {
        errorMessage = 'File not found.';
      } else if (response.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      }

      console.error('[Download] Failed with error:', errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }

    // Get content disposition filename if available
    const contentDisposition = response.headers.get('content-disposition');
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    // Get total size for progress
    const contentLength = response.headers.get('content-length');
    const total = contentLength ? parseInt(contentLength, 10) : 0;

    // Read response as blob with progress tracking
    let blob;
    if (onProgress && total > 0) {
      const reader = response.body.getReader();
      const chunks = [];
      let loaded = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        chunks.push(value);
        loaded += value.length;
        onProgress(loaded, total);
      }

      blob = new Blob(chunks);
    } else {
      blob = await response.blob();
    }

    // Create download link and trigger download
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    link.style.display = 'none';
    
    document.body.appendChild(link);
    link.click();
    
    console.log('[Download] Download triggered successfully:', filename);
    
    // Cleanup
    setTimeout(() => {
      try { document.body.removeChild(link); } catch (e) {}
      window.URL.revokeObjectURL(downloadUrl);
    }, 100);

    return {
      success: true
    };

  } catch (error) {
    console.error('[Download] Exception caught:', error);
    console.error('[Download] Error type:', error.constructor.name);
    console.error('[Download] Error stack:', error.stack);
    
    let errorMessage = 'Unexpected error while downloading.';
    
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      errorMessage = 'Connection error. Check your internet and try again.';
      console.error('[Download] Network error - possible CORS or server offline');
    } else if (error.name === 'TypeError') {
      errorMessage = 'Network error. Please check your connection.';
      console.error('[Download] TypeError - possible CORS issue');
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage
    };
  }
};

/**
 * Download the latest bot installer (public - no auth required)
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
export const downloadInstaller = async (onProgress = null) => {
  const url = `${API_BASE_URL}/bot/installer`;
  const filename = 'Unity-BOT-Installer.exe';
  return downloadFile(url, filename, false, onProgress);
};

/**
 * Download the latest bot patch (public - no auth required)
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
export const downloadPatch = async (onProgress = null) => {
  const url = `${API_BASE_URL}/bot/update/latest`;
  const filename = 'Unity-BOT-Patch.zip';
  return downloadFile(url, filename, false, onProgress);
};

/**
 * Download bot update package for a specific version (requires auth)
 * @param {string} version - Version to download (e.g., "1.2.7.8")
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
export const downloadUpdate = async (version, onProgress = null) => {
  const url = `${API_BASE_URL}/bot/updates/${encodeURIComponent(version)}/download`;
  const filename = `Unity-BOT-Update-${version}.zip`;
  return downloadFile(url, filename, true, onProgress);
};

/**
 * Show error message to user
 * @param {string} message - Error message to display
 */
export const showDownloadError = (message) => {
  // Try to use a modal if available, otherwise use alert
  const event = new CustomEvent('show-download-error', { detail: { message } });
  window.dispatchEvent(event);
  
  // Fallback to alert if no listener
  setTimeout(() => {
    if (!window.__downloadErrorHandled) {
      alert(`❌ ${message}`);
    }
    window.__downloadErrorHandled = false;
  }, 100);
};

/**
 * Show success message to user (disabled - no success messages shown)
 * @param {string} message - Success message to display
 */
export const showDownloadSuccess = (message) => {
  // Success messages disabled - do nothing
};

export default {
  downloadInstaller,
  downloadUpdate,
  showDownloadError,
  showDownloadSuccess
};
